/**
 * FikoTimeSlicedExecutor - Main thread'i bloklamadan büyük batch işlemler çalıştırır.
 * Her frame'de en fazla budgetMs kadar çalışır, sonra yield eder.
 */
export class FikoTimeSlicedExecutor {
  private budgetMs: number;
  private cancelled: boolean = false;

  constructor(budgetMs: number = 5) {
    this.budgetMs = budgetMs;
  }

  async executeBatch<T, R>(
    items: T[],
    processor: (item: T, index: number) => R,
    onProgress?: (processed: number, total: number) => void
  ): Promise<R[]> {
    this.cancelled = false;
    const results: R[] = [];
    const total = items.length;
    let processed = 0;
    let startTime = performance.now();

    for (let i = 0; i < total; i++) {
      if (this.cancelled) break;
      results.push(processor(items[i], i));
      processed++;

      const elapsed = performance.now() - startTime;
      if (elapsed >= this.budgetMs) {
        if (onProgress) onProgress(processed, total);
        await this.yieldToMainThread();
        startTime = performance.now();
      }
    }

    if (onProgress && processed > 0) onProgress(processed, total);
    return results;
  }

  async executeVoid<T>(
    items: T[],
    processor: (item: T, index: number) => void,
    onProgress?: (processed: number, total: number) => void
  ): Promise<void> {
    this.cancelled = false;
    const total = items.length;
    let processed = 0;
    let startTime = performance.now();

    for (let i = 0; i < total; i++) {
      if (this.cancelled) break;
      processor(items[i], i);
      processed++;

      const elapsed = performance.now() - startTime;
      if (elapsed >= this.budgetMs) {
        if (onProgress) onProgress(processed, total);
        await this.yieldToMainThread();
        startTime = performance.now();
      }
    }

    if (onProgress && processed > 0) onProgress(processed, total);
  }

  cancel(): void { this.cancelled = true; }
  setBudget(ms: number): void { this.budgetMs = ms; }

  /**
   * Static helper to yield control to the main thread (one frame).
   * Uses scheduler.yield() if available, otherwise setTimeout(0).
   */
  static async yieldFrame(): Promise<void> {
    if (typeof (globalThis as any).scheduler?.yield === 'function') {
      try {
        return (globalThis as any).scheduler.yield();
      } catch (e) {}
    }
    return new Promise(resolve => setTimeout(resolve, 0));
  }

  private async yieldToMainThread(): Promise<void> {
    if ('scheduler' in globalThis && 'yield' in (globalThis as any).scheduler) {
      try { await (globalThis as any).scheduler.yield(); return; } catch (e) {}
    }
    if ('requestIdleCallback' in globalThis) {
      return new Promise<void>(resolve => { requestIdleCallback(() => resolve()); });
    }
    return new Promise<void>(resolve => { setTimeout(resolve, 0); });
  }
}
